export const normalizeAssessmentType = (
  assessmentType?: string | null
): string => (assessmentType ?? "").trim().toLowerCase().replace(/\s+/g, "_");

export const isSelfAssessmentType = (assessmentType?: string | null): boolean =>
  normalizeAssessmentType(assessmentType) === "self_assessment";

export type PublishedOriginAssessment = {
  assessment_type?: string | null;
  notes?: {
    source?: string | null;
    converted_from?: string | null;
    self_assessment_id?: string | null;
  } | null;
};

/** Matches backend _level_mix_is_self_origin for hub / level-mix filtering. */
export const isPublishedSelfOrigin = (
  assessment: PublishedOriginAssessment
): boolean => {
  if (isSelfAssessmentType(assessment.assessment_type)) return true;
  const notes = assessment.notes;
  if (!notes) return false;
  if (notes.source === "self_assessment_bulk") return false;
  return (
    notes.source === "self_assessment_publish" ||
    notes.converted_from === "self_assessment"
  );
};

export const isPublishedFormalOrigin = (
  assessment: PublishedOriginAssessment
): boolean => !isPublishedSelfOrigin(assessment);

/**
 * Team Completed Re-assess lock: Self→AL already hub-published.
 * Open PRE_ACTIVE / PRE_PUBLISH `self_assessment` rows stay editable until due.
 */
export const isTeamCompletedLockedFromReassess = (
  assessment: PublishedOriginAssessment,
  publishing?: string | null
): boolean => {
  if ((publishing || "").toLowerCase() === "published") return true;
  if (isSelfAssessmentType(assessment.assessment_type)) return false;
  return isPublishedSelfOrigin(assessment);
};

const usesPublishOriginForKindTab = (tab: string): boolean =>
  tab === "completed" || tab === "published";

/** My Assessments / ESL Hub kind tabs: origin on completed & published; type elsewhere. */
export const isMyAssessmentsSelfOrigin = (
  assessment: PublishedOriginAssessment,
  tab: string
): boolean =>
  usesPublishOriginForKindTab(tab)
    ? isPublishedSelfOrigin(assessment)
    : isSelfAssessmentType(assessment.assessment_type);

export type AssessmentKindTab = "team" | "formal";

/** Parse UI/URL kind; maps legacy `self` → `team`. */
export const normalizeAssessmentKindTab = (
  kind?: string | null
): AssessmentKindTab => {
  const normalized = (kind ?? "").trim().toLowerCase();
  if (normalized === "self" || normalized === "team") return "team";
  return "formal";
};

export const matchesAssessmentKindTab = (
  assessment: PublishedOriginAssessment,
  kind: AssessmentKindTab,
  contextTab: string
): boolean => {
  const isTeam = isMyAssessmentsSelfOrigin(assessment, contextTab);
  return kind === "team" ? isTeam : !isTeam;
};

/**
 * ESL Hub disconnect kind tabs.
 * Prefer assessmentOrigin from disconnect-review (team | formal).
 * Title "Team Assessment…" wins over a stale formal origin (Accept & Publish AL rows).
 * Fall back to assessment type when origin is missing.
 */
export const matchesDisconnectKindTab = (
  assessmentType: string | null | undefined,
  kind: AssessmentKindTab,
  assessmentOrigin?: "team" | "formal" | null,
  assessmentTitle?: string | null
): boolean => {
  const titleLooksTeam = /^\s*team assessment\b/i.test(
    (assessmentTitle ?? "").trim()
  );
  let isTeam: boolean;
  if (titleLooksTeam || assessmentOrigin === "team") {
    isTeam = true;
  } else if (assessmentOrigin === "formal") {
    isTeam = false;
  } else {
    isTeam = isSelfAssessmentType(assessmentType);
  }
  return kind === "team" ? isTeam : !isTeam;
};

/** User-facing label for Formal vs Team (API may still say self). */
export const assessmentKindDisplayLabel = (
  kind?: string | null
): "Formal" | "Team" => {
  const normalized = (kind ?? "").trim().toLowerCase();
  if (
    normalized === "formal" ||
    normalized === "al" ||
    normalized === "al_assessment"
  ) {
    return "Formal";
  }
  return "Team";
};

/**
 * Rewrite legacy Self / Self-Serve assessment titles for display & create.
 * Existing DB rows often still store "Self Serve Assessment" from the template name.
 * Also strips "Team Assessment Template" → "Team Assessment".
 * When accountName is provided and the label is bare "Team Assessment",
 * returns "Team Assessment - {accountName}".
 */
export const normalizeTeamAssessmentDisplayName = (
  name?: string | null,
  fallback = "Team Assessment",
  accountName?: string | null
): string => {
  const trimmed = (name ?? "").trim();
  let next = trimmed || fallback;
  next = next.replace(/\bself[-\s]?serve\s+assessment\b/gi, "Team Assessment");
  next = next.replace(/\bself[-\s]?assessment\b/gi, "Team Assessment");
  next = next.replace(/\bself[-\s]?serve\b/gi, "Team");
  next = next.replace(/^team assessment\s+template$/i, "Team Assessment");
  next = next.trim() || fallback;
  const accountLabel = (accountName ?? "").trim();
  if (accountLabel && /^team assessment$/i.test(next)) {
    return `Team Assessment - ${accountLabel}`;
  }
  return next;
};

/** Fixed description for team assessment cards (never the template description). */
export const TEAM_ASSESSMENT_DESCRIPTION = "Team Assessment";

export const formatTeamAssessmentTitle = (
  name?: string | null,
  accountName?: string | null
): string =>
  normalizeTeamAssessmentDisplayName(name, "Team Assessment", accountName);

export type LevelMixScope = "formal" | "team" | "my";

/** Parse URL/prop scope; maps legacy org/both → formal. */
export const normalizeLevelMixScope = (
  scope?: string | null
): LevelMixScope => {
  const normalized = (scope ?? "formal").trim().toLowerCase();
  if (normalized === "org" || normalized === "both") return "formal";
  if (normalized === "team") return "team";
  if (normalized === "my") return "my";
  return "formal";
};

export const isPublishedOrgMixScope = (scope: LevelMixScope): boolean =>
  scope === "formal" || scope === "team";

export const HUB_LEVEL_MIX_SCOPES: LevelMixScope[] = ["formal", "team"];

/** Assessment Hub level-mix tab scope from URL (?mix=). */
export const parseHubLevelMixScope = (value: string | null): LevelMixScope => {
  const normalized = normalizeLevelMixScope(value);
  return HUB_LEVEL_MIX_SCOPES.includes(normalized) ? normalized : "formal";
};

export type PublishedTimestampAssessment = PublishedOriginAssessment & {
  published_at?: string | null;
  publishedAt?: string | null;
  send_at?: string | null;
  created_at?: string | null;
};

/**
 * Pick Formal vs Team for Level Mix "Published" from the newest publish time.
 * Empty / undecidable → Formal. Only one origin present → that origin.
 */
export const pickPublishedLevelMixScope = (
  assessments: PublishedTimestampAssessment[]
): "formal" | "team" => {
  let bestFormal = 0;
  let bestTeam = 0;

  for (const assessment of assessments) {
    const raw =
      assessment.published_at ||
      assessment.publishedAt ||
      assessment.send_at ||
      assessment.created_at ||
      "";
    const t = new Date(raw).getTime();
    if (!Number.isFinite(t) || t <= 0) continue;

    if (isPublishedSelfOrigin(assessment)) {
      if (t > bestTeam) bestTeam = t;
    } else if (t > bestFormal) {
      bestFormal = t;
    }
  }

  if (bestFormal === 0 && bestTeam === 0) return "formal";
  if (bestTeam === 0) return "formal";
  if (bestFormal === 0) return "team";
  return bestTeam > bestFormal ? "team" : "formal";
};

export type TeamPeerBannerVariant = "pending" | "completed";

export type TeamPeerBanner = {
  assessorName: string;
  assessedAt: string | null;
  variant: TeamPeerBannerVariant;
};

export type TeamPeerExecutive = {
  executive_id?: string | null;
  executive_name?: string | null;
  status?: string | null;
  completed_on?: string | null;
  response?: Record<string, string | string[]> | null;
};

export const hasSavedExecutiveResponse = (
  response: Record<string, string | string[]> | null | undefined
): boolean =>
  !!response &&
  typeof response === "object" &&
  Object.keys(response).length > 0;

/** First teammate who submitted on the same team-assessment round (not the current user). */
export const findPriorTeamPeer = (
  executives: TeamPeerExecutive[] | undefined,
  currentExecutiveId: string
): Omit<TeamPeerBanner, "variant"> | null => {
  if (!executives?.length || !currentExecutiveId) return null;

  const peers = executives.filter(
    exec =>
      exec.executive_id &&
      exec.executive_id !== currentExecutiveId &&
      hasSavedExecutiveResponse(exec.response) &&
      ["completed", "published"].includes((exec.status ?? "").toLowerCase())
  );
  if (!peers.length) return null;

  const peer = [...peers].sort((a, b) => {
    const aTime = a.completed_on ? new Date(a.completed_on).getTime() : 0;
    const bTime = b.completed_on ? new Date(b.completed_on).getTime() : 0;
    return aTime - bTime;
  })[0];

  return {
    assessorName: peer.executive_name?.trim() || "A teammate",
    assessedAt: peer.completed_on ?? null,
  };
};

/**
 * Pending-input banner when a teammate already submitted and the current user
 * still needs to complete (or continue) their team assessment.
 */
export const findPendingTeamPeerBanner = (
  executives: TeamPeerExecutive[] | undefined,
  currentExecutiveId: string
): TeamPeerBanner | null => {
  const peer = findPriorTeamPeer(executives, currentExecutiveId);
  if (!peer) return null;

  const me = executives?.find(exec => exec.executive_id === currentExecutiveId);
  const myStatus = (me?.status ?? "").toLowerCase();
  if (myStatus === "completed" || myStatus === "published") return null;

  return { ...peer, variant: "pending" };
};
